/* eslint-disable max-len */
import React, { useState, useEffect } from "react";
import { SvgIcon, SvgIconProps } from "@mui/material";

interface EcoscoreIconProps extends Omit<SvgIconProps, "viewBox"> {
  score: 0 | 1 | 2 | 3 | 4 | 5;
  category?: "soil" | "air" | "water" | "succession" | "biodiversity" | "structure" | "forest";
}

/**
 * EcoscoreIcon - An animated circular progress indicator showing ecoscore levels from 0-5
 *
 * @param score - The ecoscore level (0-5)
 * @param category - The category type (soil, air, water, succession, biodiversity)
 * @param props - Additional SvgIcon props (sx, fontSize, etc.)
 *
 * Visual representation (Original colors, animated arc):
 * - 0: Empty circle with black outline + center badge with category icon
 * - 1-4: Animated progressive gradient arc (#D1EFFF → #959A80) with a white marker dot that appears after the animation + center badge
 * - 5: Animated complete circle (solid #D1EFFF) + center badge with inverted icon color
 */
const EcoscoreIcon: React.FC<EcoscoreIconProps> = ({ score, category, ...props }) => {
  const gradientId = React.useMemo(() => `ecoscore-gradient-${Math.random().toString(36).substr(2, 9)}`, []);

  // --- SVG & Animation Constants ---
  const center = 52.5;
  const radius = 45;
  const strokeWidth = 14;
  const circumference = 2 * Math.PI * radius;
  const animationDuration = 800; // in milliseconds
  const initialDelay = 100; // in milliseconds

  // --- Animation State ---
  const [strokeOffset, setStrokeOffset] = useState(circumference);
  const [showDot, setShowDot] = useState(false); // State to control dot visibility

  useEffect(() => {
    // Hide the dot immediately whenever the score changes
    setShowDot(false);

    const targetOffset = circumference - (score / 5) * circumference;

    // Timer to start the arc animation
    const animationTimer = setTimeout(() => {
      setStrokeOffset(targetOffset);
    }, initialDelay);

    // Timer to show the dot after the arc animation has finished
    const dotTimer = setTimeout(() => {
      if (score > 0 && score < 5) {
        setShowDot(true);
      }
    }, initialDelay + animationDuration); // Total time until dot appears

    // Cleanup timers on component unmount or score change
    return () => {
      clearTimeout(animationTimer);
      clearTimeout(dotTimer);
    };
  }, [score, circumference]);

  // Renders the category-specific icon in the center badge
  const renderCenterIcon = () => {
    const iconProps = {
      fill: score === 5 ? "#D1EFFF" : "#323232",
      width: "24",
      height: "24",
    };

    switch (category) {
      case "soil":
        return (
          <g transform="translate(43.5, 43.3)">
            <path
              d="M9.60066 16.0595C9.24066 16.3395 8.73066 16.3395 8.37066 16.0595L2.22066 11.2795C1.86066 10.9995 1.36066 10.9995 1.00066 11.2795C0.490664 11.6795 0.490664 12.4495 1.00066 12.8495L7.76066 18.1095C8.48066 18.6695 9.49066 18.6695 10.2207 18.1095L16.9807 12.8495C17.4907 12.4495 17.4907 11.6795 16.9807 11.2795L16.9707 11.2695C16.6107 10.9895 16.1107 10.9895 15.7507 11.2695L9.60066 16.0595ZM10.2307 13.0395L16.9907 7.77953C17.5007 7.37953 17.5007 6.59953 16.9907 6.19953L10.2307 0.939531C9.51066 0.379531 8.50066 0.379531 7.77066 0.939531L1.01066 6.20953C0.500664 6.60953 0.500664 7.38953 1.01066 7.78953L7.77066 13.0495C8.49066 13.6095 9.51066 13.6095 10.2307 13.0395Z"
              fill={iconProps.fill}
            />
          </g>
        );
      case "air":
        return (
          <g transform="translate(42.5, 44.3)">
            <path
              d="M9.5 17C8.66667 17 7.95833 16.7083 7.375 16.125C6.79167 15.5417 6.5 14.8333 6.5 14H8.5C8.5 14.2833 8.59583 14.5208 8.7875 14.7125C8.97917 14.9042 9.21667 15 9.5 15C9.78333 15 10.0208 14.9042 10.2125 14.7125C10.4042 14.5208 10.5 14.2833 10.5 14C10.5 13.7167 10.4042 13.4792 10.2125 13.2875C10.0208 13.0958 9.78333 13 9.5 13H0V11H9.5C10.3333 11 11.0417 11.2917 11.625 11.875C12.2083 12.4583 12.5 13.1667 12.5 14C12.5 14.8333 12.2083 15.5417 11.625 16.125C11.0417 16.7083 10.3333 17 9.5 17ZM0 7V5H13.5C13.9333 5 14.2917 4.85833 14.575 4.575C14.8583 4.29167 15 3.93333 15 3.5C15 3.06667 14.8583 2.70833 14.575 2.425C14.2917 2.14167 13.9333 2 13.5 2C13.0667 2 12.7083 2.14167 12.425 2.425C12.1417 2.70833 12 3.06667 12 3.5H10C10 2.51667 10.3375 1.6875 11.0125 1.0125C11.6875 0.3375 12.5167 0 13.5 0C14.4833 0 15.3125 0.3375 15.9875 1.0125C16.6625 1.6875 17 2.51667 17 3.5C17 4.48333 16.6625 5.3125 15.9875 5.9875C15.3125 6.6625 14.4833 7 13.5 7H0ZM16.5 15V13C16.9333 13 17.2917 12.8583 17.575 12.575C17.8583 12.2917 18 11.9333 18 11.5C18 11.0667 17.8583 10.7083 17.575 10.425C17.2917 10.1417 16.9333 10 16.5 10H0V8H16.5C17.4833 8 18.3125 8.3375 18.9875 9.0125C19.6625 9.6875 20 10.5167 20 11.5C20 12.4833 19.6625 13.3125 18.9875 13.9875C18.3125 14.6625 17.4833 15 16.5 15Z"
              fill={iconProps.fill}
            />
          </g>
        );
      case "water":
        return (
          <g transform="translate(44.5, 42.8)">
            <path
              d="M8.275 17C8.475 16.9833 8.64583 16.9042 8.7875 16.7625C8.92917 16.6208 9 16.45 9 16.25C9 16.0167 8.925 15.8292 8.775 15.6875C8.625 15.5458 8.43333 15.4833 8.2 15.5C7.51667 15.55 6.79167 15.3625 6.025 14.9375C5.25833 14.5125 4.775 13.7417 4.575 12.625C4.54167 12.4417 4.45417 12.2917 4.3125 12.175C4.17083 12.0583 4.00833 12 3.825 12C3.59167 12 3.4 12.0875 3.25 12.2625C3.1 12.4375 3.05 12.6417 3.1 12.875C3.38333 14.3917 4.05 15.475 5.1 16.125C6.15 16.775 7.20833 17.0667 8.275 17ZM8 20C5.71667 20 3.8125 19.2167 2.2875 17.65C0.7625 16.0833 0 14.1333 0 11.8C0 10.1333 0.6625 8.32083 1.9875 6.3625C3.3125 4.40417 5.31667 2.28333 8 0C10.6833 2.28333 12.6875 4.40417 14.0125 6.3625C15.3375 8.32083 16 10.1333 16 11.8C16 14.1333 15.2375 16.0833 13.7125 17.65C12.1875 19.2167 10.2833 20 8 20ZM8 18C9.73333 18 11.1667 17.4125 12.3 16.2375C13.4333 15.0625 14 13.5833 14 11.8C14 10.5833 13.4958 9.20833 12.4875 7.675C11.4792 6.14167 9.98333 4.46667 8 2.65C6.01667 4.46667 4.52083 6.14167 3.5125 7.675C2.50417 9.20833 2 10.5833 2 11.8C2 13.5833 2.56667 15.0625 3.7 16.2375C4.83333 17.4125 6.26667 18 8 18Z"
              fill={iconProps.fill}
            />
          </g>
        );
      case "succession":
        return (
          <g transform="translate(41.5, 41.5)">
            <path
              d="M11 21V13.025C9.93333 13.025 8.90833 12.8208 7.925 12.4125C6.94167 12.0042 6.075 11.425 5.325 10.675C4.575 9.925 4 9.05833 3.6 8.075C3.2 7.09167 3 6.06667 3 5V3H5C6.05 3 7.06667 3.20417 8.05 3.6125C9.03333 4.02083 9.9 4.6 10.65 5.35C11.1667 5.86667 11.5958 6.43333 11.9375 7.05C12.2792 7.66667 12.5417 8.325 12.725 9.025C12.8083 8.90833 12.9 8.79583 13 8.6875C13.1 8.57917 13.2083 8.46667 13.325 8.35C14.075 7.6 14.9417 7.02083 15.925 6.6125C16.9083 6.20417 17.9333 6 19 6H21V8C21 9.06667 20.7958 10.0917 20.3875 11.075C19.9792 12.0583 19.4 12.925 18.65 13.675C17.9 14.425 17.0375 15 16.0625 15.4C15.0875 15.8 14.0667 16 13 16V21H11ZM11 11C11 10.2 10.8458 9.4375 10.5375 8.7125C10.2292 7.9875 9.79167 7.34167 9.225 6.775C8.65833 6.20833 8.0125 5.77083 7.2875 5.4625C6.5625 5.15417 5.8 5 5 5C5 5.8 5.15 6.56667 5.45 7.3C5.75 8.03333 6.18333 8.68333 6.75 9.25C7.31667 9.81667 7.96667 10.25 8.7 10.55C9.43333 10.85 10.2 11 11 11ZM13 14C13.8 14 14.5625 13.85 15.2875 13.55C16.0125 13.25 16.6583 12.8167 17.225 12.25C17.7917 11.6833 18.2292 11.0333 18.5375 10.3C18.8458 9.56667 19 8.8 19 8C18.2 8 17.4333 8.15417 16.7 8.4625C15.9667 8.77083 15.3167 9.20833 14.75 9.775C14.1833 10.3417 13.75 10.9875 13.45 11.7125C13.15 12.4375 13 13.2 13 14Z"
              fill={iconProps.fill}
            />
          </g>
        );
      case "structure":
        return (
          <g transform="translate(41.5, 41.5)">
            <path
              d="M11 21V13.025C9.93333 13.025 8.90833 12.8208 7.925 12.4125C6.94167 12.0042 6.075 11.425 5.325 10.675C4.575 9.925 4 9.05833 3.6 8.075C3.2 7.09167 3 6.06667 3 5V3H5C6.05 3 7.06667 3.20417 8.05 3.6125C9.03333 4.02083 9.9 4.6 10.65 5.35C11.1667 5.86667 11.5958 6.43333 11.9375 7.05C12.2792 7.66667 12.5417 8.325 12.725 9.025C12.8083 8.90833 12.9 8.79583 13 8.6875C13.1 8.57917 13.2083 8.46667 13.325 8.35C14.075 7.6 14.9417 7.02083 15.925 6.6125C16.9083 6.20417 17.9333 6 19 6H21V8C21 9.06667 20.7958 10.0917 20.3875 11.075C19.9792 12.0583 19.4 12.925 18.65 13.675C17.9 14.425 17.0375 15 16.0625 15.4C15.0875 15.8 14.0667 16 13 16V21H11ZM11 11C11 10.2 10.8458 9.4375 10.5375 8.7125C10.2292 7.9875 9.79167 7.34167 9.225 6.775C8.65833 6.20833 8.0125 5.77083 7.2875 5.4625C6.5625 5.15417 5.8 5 5 5C5 5.8 5.15 6.56667 5.45 7.3C5.75 8.03333 6.18333 8.68333 6.75 9.25C7.31667 9.81667 7.96667 10.25 8.7 10.55C9.43333 10.85 10.2 11 11 11ZM13 14C13.8 14 14.5625 13.85 15.2875 13.55C16.0125 13.25 16.6583 12.8167 17.225 12.25C17.7917 11.6833 18.2292 11.0333 18.5375 10.3C18.8458 9.56667 19 8.8 19 8C18.2 8 17.4333 8.15417 16.7 8.4625C15.9667 8.77083 15.3167 9.20833 14.75 9.775C14.1833 10.3417 13.75 10.9875 13.45 11.7125C13.15 12.4375 13 13.2 13 14Z"
              fill={iconProps.fill}
            />
          </g>
        );

      case "forest":
        return (
          <g transform="translate(41.5, 41.5)">
            <path
              d="M11 21V13.025C9.93333 13.025 8.90833 12.8208 7.925 12.4125C6.94167 12.0042 6.075 11.425 5.325 10.675C4.575 9.925 4 9.05833 3.6 8.075C3.2 7.09167 3 6.06667 3 5V3H5C6.05 3 7.06667 3.20417 8.05 3.6125C9.03333 4.02083 9.9 4.6 10.65 5.35C11.1667 5.86667 11.5958 6.43333 11.9375 7.05C12.2792 7.66667 12.5417 8.325 12.725 9.025C12.8083 8.90833 12.9 8.79583 13 8.6875C13.1 8.57917 13.2083 8.46667 13.325 8.35C14.075 7.6 14.9417 7.02083 15.925 6.6125C16.9083 6.20417 17.9333 6 19 6H21V8C21 9.06667 20.7958 10.0917 20.3875 11.075C19.9792 12.0583 19.4 12.925 18.65 13.675C17.9 14.425 17.0375 15 16.0625 15.4C15.0875 15.8 14.0667 16 13 16V21H11ZM11 11C11 10.2 10.8458 9.4375 10.5375 8.7125C10.2292 7.9875 9.79167 7.34167 9.225 6.775C8.65833 6.20833 8.0125 5.77083 7.2875 5.4625C6.5625 5.15417 5.8 5 5 5C5 5.8 5.15 6.56667 5.45 7.3C5.75 8.03333 6.18333 8.68333 6.75 9.25C7.31667 9.81667 7.96667 10.25 8.7 10.55C9.43333 10.85 10.2 11 11 11ZM13 14C13.8 14 14.5625 13.85 15.2875 13.55C16.0125 13.25 16.6583 12.8167 17.225 12.25C17.7917 11.6833 18.2292 11.0333 18.5375 10.3C18.8458 9.56667 19 8.8 19 8C18.2 8 17.4333 8.15417 16.7 8.4625C15.9667 8.77083 15.3167 9.20833 14.75 9.775C14.1833 10.3417 13.75 10.9875 13.45 11.7125C13.15 12.4375 13 13.2 13 14Z"
              fill={iconProps.fill}
            />
          </g>
        );
      case "biodiversity":
        return (
          <g transform="translate(41, 41)">
            <path
              d="M5.5 22C4.53333 22 3.70833 21.6583 3.025 20.975C2.34167 20.2917 2 19.4667 2 18.5C2 17.5333 2.34167 16.7083 3.025 16.025C3.70833 15.3417 4.53333 15 5.5 15C5.8 15 6.09167 15.0375 6.375 15.1125C6.65833 15.1875 6.925 15.2917 7.175 15.425L11 11.6V8.85C10.2667 8.63333 9.66667 8.22083 9.2 7.6125C8.73333 7.00417 8.5 6.3 8.5 5.5C8.5 4.53333 8.84167 3.70833 9.525 3.025C10.2083 2.34167 11.0333 2 12 2C12.9667 2 13.7917 2.34167 14.475 3.025C15.1583 3.70833 15.5 4.53333 15.5 5.5C15.5 6.3 15.2667 7.00417 14.8 7.6125C14.3333 8.22083 13.7333 8.63333 13 8.85V11.6L16.85 15.425C17.1 15.2917 17.3625 15.1875 17.6375 15.1125C17.9125 15.0375 18.2 15 18.5 15C19.4667 15 20.2917 15.3417 20.975 16.025C21.6583 16.7083 22 17.5333 22 18.5C22 19.4667 21.6583 20.2917 20.975 20.975C20.2917 21.6583 19.4667 22 18.5 22C17.5333 22 16.7083 21.6583 16.025 20.975C15.3417 20.2917 15 19.4667 15 18.5C15 18.2 15.0375 17.9083 15.1125 17.625C15.1875 17.3417 15.2917 17.075 15.425 16.825L12 13.4L8.575 16.825C8.70833 17.075 8.8125 17.3417 8.8875 17.625C8.9625 17.9083 9 18.2 9 18.5C9 19.4667 8.65833 20.2917 7.975 20.975C7.29167 21.6583 6.46667 22 5.5 22ZM18.5 20C18.9167 20 19.2708 19.8542 19.5625 19.5625C19.8542 19.2708 20 18.9167 20 18.5C20 18.0833 19.8542 17.7292 19.5625 17.4375C19.2708 17.1458 18.9167 17 18.5 17C18.0833 17 17.7292 17.1458 17.4375 17.4375C17.1458 17.7292 17 18.0833 17 18.5C17 18.9167 17.1458 19.2708 17.4375 19.5625C17.7292 19.8542 18.0833 20 18.5 20ZM12 7C12.4167 7 12.7708 6.85417 13.0625 6.5625C13.3542 6.27083 13.5 5.91667 13.5 5.5C13.5 5.08333 13.3542 4.72917 13.0625 4.4375C12.7708 4.14583 12.4167 4 12 4C11.5833 4 11.2292 4.14583 10.9375 4.4375C10.6458 4.72917 10.5 5.08333 10.5 5.5C10.5 5.91667 10.6458 6.27083 10.9375 6.5625C11.2292 6.85417 11.5833 7 12 7ZM5.5 20C5.91667 20 6.27083 19.8542 6.5625 19.5625C6.85417 19.2708 7 18.9167 7 18.5C7 18.0833 6.85417 17.7292 6.5625 17.4375C6.27083 17.1458 5.91667 17 5.5 17C5.08333 17 4.72917 17.1458 4.4375 17.4375C4.14583 17.7292 4 18.0833 4 18.5C4 18.9167 4.14583 19.2708 4.4375 19.5625C4.72917 19.8542 5.08333 20 5.5 20Z"
              fill={iconProps.fill}
            />
          </g>
        );
      default:
        return (
          <g transform="translate(41.5, 41.5)">
            <path
              d="M11 21V13.025C9.93333 13.025 8.90833 12.8208 7.925 12.4125C6.94167 12.0042 6.075 11.425 5.325 10.675C4.575 9.925 4 9.05833 3.6 8.075C3.2 7.09167 3 6.06667 3 5V3H5C6.05 3 7.06667 3.20417 8.05 3.6125C9.03333 4.02083 9.9 4.6 10.65 5.35C11.1667 5.86667 11.5958 6.43333 11.9375 7.05C12.2792 7.66667 12.5417 8.325 12.725 9.025C12.8083 8.90833 12.9 8.79583 13 8.6875C13.1 8.57917 13.2083 8.46667 13.325 8.35C14.075 7.6 14.9417 7.02083 15.925 6.6125C16.9083 6.20417 17.9333 6 19 6H21V8C21 9.06667 20.7958 10.0917 20.3875 11.075C19.9792 12.0583 19.4 12.925 18.65 13.675C17.9 14.425 17.0375 15 16.0625 15.4C15.0875 15.8 14.0667 16 13 16V21H11ZM11 11C11 10.2 10.8458 9.4375 10.5375 8.7125C10.2292 7.9875 9.79167 7.34167 9.225 6.775C8.65833 6.20833 8.0125 5.77083 7.2875 5.4625C6.5625 5.15417 5.8 5 5 5C5 5.8 5.15 6.56667 5.45 7.3C5.75 8.03333 6.18333 8.68333 6.75 9.25C7.31667 9.81667 7.96667 10.25 8.7 10.55C9.43333 10.85 10.2 11 11 11ZM13 14C13.8 14 14.5625 13.85 15.2875 13.55C16.0125 13.25 16.6583 12.8167 17.225 12.25C17.7917 11.6833 18.2292 11.0333 18.5375 10.3C18.8458 9.56667 19 8.8 19 8C18.2 8 17.4333 8.15417 16.7 8.4625C15.9667 8.77083 15.3167 9.20833 14.75 9.775C14.1833 10.3417 13.75 10.9875 13.45 11.7125C13.15 12.4375 13 13.2 13 14Z"
              fill={iconProps.fill}
            />
          </g>
        );
    }
  };

  const renderMarkerDot = () => {
    // Check the showDot state before rendering
    if (!showDot) return null;

    const angleInDegrees = (score / 5) * 360 - 90;
    const angleInRadians = (angleInDegrees * Math.PI) / 180;
    const cx = center + radius * Math.cos(angleInRadians);
    const cy = center + radius * Math.sin(angleInRadians);

    return (
      <circle
        cx={cx}
        cy={cy}
        r="7"
        fill="white"
        style={{
          opacity: showDot ? 1 : 0,
          transition: "opacity 0.3s ease-in",
        }}
      />
    );
  };

  const svgContent = () => {
    if (score === 0) {
      return (
        <g>
          <circle cx={center} cy={center} r={radius} stroke="black" strokeWidth={strokeWidth} fill="none" />
          <circle cx={center} cy={center} r="19.5" fill="#959A80" />
          {renderCenterIcon()}
        </g>
      );
    }

    return (
      <g>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#D1EFFF" />
            <stop offset="100%" stopColor="#959A80" />
          </linearGradient>
        </defs>

        {score < 5 && (
          <circle cx={center} cy={center} r={radius} fill="none" stroke="black" strokeWidth={strokeWidth} />
        )}

        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={score === 5 ? "#D1EFFF" : `url(#${gradientId})`}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeOffset}
          transform={`rotate(-90 ${center} ${center})`}
          style={{
            transition: `stroke-dashoffset ${animationDuration}ms ease-in-out, stroke 0.5s ease`,
          }}
        />

        {renderMarkerDot()}

        <circle cx={center} cy={center} r="19.5" fill="#959A80" />
        {renderCenterIcon()}
      </g>
    );
  };

  return (
    <SvgIcon {...props} viewBox="0 0 105 105" sx={{ width: "105px", height: "105px", ...props.sx }}>
      {svgContent()}
    </SvgIcon>
  );
};

export default EcoscoreIcon;
